import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaArrowRight, 
  FaReact, 
  FaNodeJs, 
  FaDatabase,
  FaMobile,
  FaRocket,
  FaShieldAlt,
  FaClock,
  FaUsers,
  FaQuoteLeft,
  FaStar,
  FaChartLine,
  FaUsersCog
} from 'react-icons/fa';
import { SiTypescript, SiNextdotjs, SiMongodb, SiMysql, SiVuedotjs, SiExpress, SiPostgresql, SiDocker } from 'react-icons/si';
import { serviceSeoData, seoImage } from '../../config/pageSeoConfig';
import './WebDevelopment.scss';

const WebDevelopment: React.FC = () => {
  // SEO constants from config
  const seoTitle = serviceSeoData.webDevelopment.title;
  const seoDescription = serviceSeoData.webDevelopment.description;
  const seoKeywords = serviceSeoData.webDevelopment.keywords;
  const seoUrl = serviceSeoData.webDevelopment.url;
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Web Development Services",
    "description": seoDescription,
    "provider": {
      "@type": "Organization",
      "name": "App Developer Pro",
      "url": "https://appdeveloperpro.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://appdeveloperpro.com/images/team-at-work-app-developer-pro.jpg"
      }
    },
    "areaServed": "Worldwide",
    "serviceType": "Web Development, Website Development, Web Application Development, Custom Web Solutions, SaaS, eCommerce, Enterprise Web Apps",
    "url": seoUrl,
    "image": seoImage
  };
  const features = [
    {
      icon: FaReact,
      title: 'Modern Frontend',
      description: 'Built with React, Angular, or Vue.js for dynamic, interactive user interfaces'
    },
    {
      icon: FaNodeJs,
      title: 'Robust Backend',
      description: 'Scalable server-side solutions using Node.js, Python, or PHP'
    },
    {
      icon: FaDatabase,
      title: 'Database Integration',
      description: 'Secure data management with MongoDB, MySQL, or PostgreSQL'
    },
    {
      icon: FaMobile,
      title: 'Responsive Design',
      description: 'Mobile-first approach ensuring perfect display on all devices'
    },
    {
      icon: FaRocket,
      title: 'Performance Focused',
      description: 'Optimized for speed with 95%+ performance scores'
    },
    {
      icon: FaShieldAlt,
      title: 'Security & SEO',
      description: 'SSL encryption, secure coding practices, and search engine optimization built-in'
    }
  ];

  const technologies = [
    { icon: FaReact, name: 'React', color: '#61DAFB' },
    { icon: SiNextdotjs, name: 'Next.js', color: '#000000' },
    { icon: SiTypescript, name: 'TypeScript', color: '#3178C6' },
    { icon: FaNodeJs, name: 'Node.js', color: '#339933' },
    { icon: SiMongodb, name: 'MongoDB', color: '#47A248' },
    { icon: SiMysql, name: 'MySQL', color: '#4479A1' },
    { icon: SiVuedotjs, name: 'Vue.js', color: '#4FC08D' },
    { icon: SiExpress, name: 'Express.js', color: '#000000' },
    { icon: SiPostgresql, name: 'PostgreSQL', color: '#336791' },
    { icon: SiDocker, name: 'Docker', color: '#2496ED' }
  ];

  const benefits = [
    {
      icon: FaShieldAlt,
      title: 'Security First',
      description: 'Enterprise-grade security measures to protect your data and users'
    },
    {
      icon: FaClock,
      title: 'Fast Delivery',
      description: 'Agile development process with regular updates and quick turnaround'
    },
    {
      icon: FaUsers,
      title: 'Ongoing Support',
      description: '24/7 maintenance and support to keep your website running smoothly'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Planning',
      description: 'We analyze your requirements and create a detailed project roadmap'
    },
    {
      step: '02',
      title: 'Design & Prototype',
      description: 'Create wireframes and prototypes to visualize your website'
    },
    {
      step: '03',
      title: 'Development',
      description: 'Build your website using modern technologies and best practices'
    },
    {
      step: '04',
      title: 'Testing & Launch',
      description: 'Thorough testing followed by deployment and go-live support'
    }
  ];

  const completedProjects = [
    {
      title: 'Corporate Business Website',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop',
      technologies: [
        { name: 'Next.js', icon: SiNextdotjs, color: '#000000' },
        { name: 'TypeScript', icon: SiTypescript, color: '#3178C6' },
        { name: 'PostgreSQL', icon: SiPostgresql, color: '#336791' }
      ],
      description: 'Enterprise website with CMS, multi-language support, and advanced SEO',
      metrics: {
        pageSpeed: '98/100',
        visitors: '50K/mo',
        conversion: '4.2%'
      }
    },
    {
      title: 'Real Estate Portal',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&auto=format&fit=crop',
      technologies: [
        { name: 'React', icon: FaReact, color: '#61DAFB' },
        { name: 'Node.js', icon: FaNodeJs, color: '#339933' },
        { name: 'MongoDB', icon: SiMongodb, color: '#47A248' }
      ],
      description: 'Property listing platform with search filters, virtual tours, and CRM integration',
      metrics: {
        listings: '5K+',
        users: '15K+',
        searches: '100K/mo'
      }
    },
    {
      title: 'Educational Learning Platform',
      image: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&auto=format&fit=crop',
      technologies: [
        { name: 'Vue.js', icon: SiVuedotjs, color: '#4FC08D' },
        { name: 'Express.js', icon: SiExpress, color: '#000000' },
        { name: 'MySQL', icon: SiMysql, color: '#4479A1' }
      ],
      description: 'Online learning platform with video streaming, quizzes, and progress tracking',
      metrics: {
        students: '25K+',
        courses: '500+',
        satisfaction: '4.8/5'
      }
    }
  ];

  const testimonials = [
    {
      text: "The web development team delivered beyond our expectations. Our new website is fast, beautiful, and has increased our conversions by 40%.",
      author: "Sarah Johnson",
      company: "Tech Startup",
      rating: 5
    },
    {
      text: "Professional, reliable, and incredibly skilled. They transformed our outdated website into a modern, high-performing platform.",
      author: "Mike Chen",
      company: "E-commerce Business",
      rating: 5
    }
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="web development, website development, web application, custom website, react development, node.js, next.js, ecommerce development, saas, enterprise web apps, web portal, 2025" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="App Developer Pro" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:site" content="@appdeveloperpro" />
        {/* Schema.org Markup */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="web-development">
      {/* Hero Section */}
      <section className="web-development__hero">
        <div className="web-development__hero-content">
          <motion.div
            className="web-development__hero-text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="web-development__hero-title">
              Custom <span className="home__hero-highlight">Web Development </span> Services
            </h1>
            <h2>React, Angular, Vue.Js, Node.JS, Python, PHP Solutions🤝</h2>
            <p className="web-development__hero-subtitle">
             App Developer Pro designs & develops high performance, websites that align with you business needs and goals. From intuitive user interfaces to secure, scalable backend systems, my custom web development solutions ensure seamless functionality, exceptional user experience, and long-term growth. Whether you're launching a new product or upgrading an existing platform, we deliver modern, reliable, and future-ready web solutions that drive real impact.
            </p>
            <div className="web-development__hero-buttons">
              <Link to="/contact" className="web-development__hero-btn web-development__hero-btn--primary">
                Start Your Project <FaArrowRight />
              </Link>
              <Link to="/portfolio" className="web-development__hero-btn web-development__hero-btn--secondary">
                View Our Work
              </Link>
            </div>
          </motion.div>
          <motion.div
            className="web-development__hero-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="web-development__hero-code">
              <div className="web-development__hero-code-header">
                <div className="web-development__hero-code-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span>App.tsx</span>
              </div>
              <div className="web-development__hero-code-content">
                <div className="web-development__hero-code-line">
                  <span className="keyword">import</span> <span className="variable">React</span> <span className="keyword">from</span> <span className="string">'react'</span>;
                </div>
                <div className="web-development__hero-code-line">
                  <span className="keyword">const</span> <span className="function">App</span> = () =&gt; &#123;
                </div>
                <div className="web-development__hero-code-line">
                  &nbsp;&nbsp;<span className="keyword">return</span> (
                </div>
                <div className="web-development__hero-code-line">
                  &nbsp;&nbsp;&nbsp;&nbsp;&lt;<span className="tag">div</span> <span className="attribute">className</span>=<span className="string">"app"</span>&gt;
                </div>
                <div className="web-development__hero-code-line">
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;<span className="tag">h1</span>&gt;Your Dream Website&lt;/<span className="tag">h1</span>&gt;
                </div>
                <div className="web-development__hero-code-line">
                  &nbsp;&nbsp;&nbsp;&nbsp;&lt;/<span className="tag">div</span>&gt;
                </div>
                <div className="web-development__hero-code-line">
                  &nbsp;&nbsp;);
                </div>
                <div className="web-development__hero-code-line">
                  &#125;;
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="web-development__features">
        <div className="web-development__section-header">
          <motion.h2
            className="web-development__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            What App Developer Pro Deliver ?
          </motion.h2>
          <motion.p
            className="web-development__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
           App Developer Pro is the well knowing name for high-performance, and fully responsive website development services using the latest technologies. Whether you need a simple business website or a complex enterprise platform, we deliver scalable solutions that enhance user experience, improve efficiency, and drive growth.

          </motion.p>
        </div>
        <div className="web-development__features-grid">
          {features.map((feature, index) => {
            const FeatureIcon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                className="web-development__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="web-development__feature-header">
                  <div className="web-development__feature-icon">
                    <FeatureIcon />
                  </div>
                  <h3 className="web-development__feature-title">{feature.title}</h3>
                </div>
                <p className="web-development__feature-description">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Technologies Section */}
      <section className="web-development__technologies">
        <motion.div
          className="web-development__section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="web-development__section-title">Technologies We Use</h2>
          <p className="web-development__section-subtitle">
            Cutting-edge tools and frameworks for modern web development
          </p>
        </motion.div>
        <div className="web-development__technologies-grid">
          {technologies.map((tech, index) => {
            const TechIcon = tech.icon;
            return (
              <motion.div
                key={tech.name}
                className="web-development__technology-item"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.1, y: -5 }}
              >
                <div 
                  className="web-development__technology-icon"
                  style={{ '--tech-color': tech.color } as React.CSSProperties}
                >
                  <TechIcon />
                </div>
                <span className="web-development__technology-name">{tech.name}</span>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="web-development__benefits">
        <div className="web-development__section-header">
          <motion.h2
            className="web-development__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why Choose Our Web Development Services
          </motion.h2>
        </div>
        <div className="web-development__benefits-grid">
          {benefits.map((benefit, index) => {
            const BenefitIcon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                className="web-development__benefit-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="web-development__benefit-icon">
                  <BenefitIcon />
                </div>
                <h3 className="web-development__benefit-title">{benefit.title}</h3>
                <p className="web-development__benefit-description">{benefit.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Completed Projects Section */}
      <section className="web-development__process">
        <div className="web-development__section-header">
          <motion.h2
            className="web-development__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Completed Latest Projects
          </motion.h2>
          <motion.p
            className="web-development__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Real websites we've built for real clients
          </motion.p>
        </div>
        <div 
          className="web-development__projects-container"
          style={{
            display: 'flex',
            flexDirection: 'row' as const,
            flexWrap: 'nowrap' as const,
            gap: '30px',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 20px'
          }}
        >
          {completedProjects.map((project, index) => (
            <motion.div
              key={project.title}
              className="web-development__project-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              style={{
                flex: '1 1 calc(33.333% - 20px)',
                minWidth: '300px',
                maxWidth: 'calc(33.333% - 20px)',
                background: 'white',
                borderRadius: '15px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              <div 
                className="web-development__project-image"
                style={{
                  position: 'relative',
                  height: '250px',
                  overflow: 'hidden'
                }}
              >
                <img 
                  src={project.image} 
                  alt={project.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <div 
                  className="web-development__project-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to top, rgba(103, 58, 183, 0.9), transparent)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    padding: '20px',
                    gap: '8px'
                  }}
                >
                  {project.technologies.map((tech) => {
                    const TechIcon = tech.icon;
                    return (
                      <span
                        key={tech.name}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '5px 12px',
                          background: 'rgba(255,255,255,0.95)',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: tech.color
                        }}
                      >
                        <TechIcon /> {tech.name}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div 
                className="web-development__project-content"
                style={{
                  padding: '25px'
                }}
              >
                <h3 
                  style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    marginBottom: '12px',
                    color: '#2c3e50'
                  }}
                >
                  {project.title}
                </h3>
                <p 
                  style={{
                    fontSize: '15px',
                    lineHeight: '1.6',
                    color: '#555',
                    marginBottom: '20px'
                  }}
                >
                  {project.description}
                </p>
                <div 
                  className="web-development__project-metrics"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: '20px',
                    borderTop: '1px solid #eee'
                  }}
                >
                  {Object.entries(project.metrics).map(([key, value]) => (
                    <div 
                      key={key}
                      style={{
                        textAlign: 'center'
                      }}
                    >
                      <div 
                        style={{
                          fontSize: '18px',
                          fontWeight: '700',
                          color: '#673ab7',
                          marginBottom: '5px'
                        }}
                      >
                        {value}
                      </div>
                      <div 
                        style={{
                          fontSize: '12px',
                          color: '#888',
                          textTransform: 'capitalize'
                        }}
                      >
                        {key}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="web-development__testimonials">
        <div className="web-development__section-header">
          <motion.h2
            className="web-development__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Client Success Stories
          </motion.h2>
        </div>
        <div className="web-development__testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              className="web-development__testimonial-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="web-development__testimonial-quote">
                <FaQuoteLeft />
              </div>
              <div className="web-development__testimonial-stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <p className="web-development__testimonial-text">"{testimonial.text}"</p>
              <div className="web-development__testimonial-author">
                <strong>{testimonial.author}</strong>
                <span>{testimonial.company}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ Section - SEO Boost */}
      <section className="web-development__faq">
        <div className="container">
          <motion.div
            className="web-development__section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Frequently Asked Questions About Web Development</h2>
            <p>Everything you need to know about professional web development services</p>
          </motion.div>
          <div className="web-development__faq-list">
            <motion.div
              className="web-development__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>How much does it cost to build a professional website?</h3>
              <p>As compare to market price, App Developer Pro delivers high performance websites at very low cost. Here website costs vary based on complexity, features & website types like static or dynamic. A static business website (10-15 pages) starts at $300-$500 and basic dynamic business website starts at $400-$1000+. App Developer Pro provides detailed quotes after understanding your specific requirements during a free consultation.</p>
            </motion.div>
            <motion.div
              className="web-development__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>How long does it take to develop a website?</h3>
              <p>Development timelines depend on project scope. A simple brochure website takes 1-2 weeks, a business website with custom features takes 2-4 weeks, and complex web development take 4-8 weeks. We provide a detailed timeline during project planning.</p>
            </motion.div>
            <motion.div
              className="web-development__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>Do you provide website maintenance and support?</h3>
              <p>Yes, App Developer Pro gives you first year free post launched maintenance & technical supports for all packages . After that, you need to pay as per your business requirements.</p>
            </motion.div>
            <motion.div
              className="web-development__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3>Will my website be mobile-friendly and SEO-optimized?</h3>
              <p>Absolutely! All our websites are built with responsive design, ensuring perfect display on all devices. App Developer Pro implements SEO best practices including fast loading speeds, clean code, proper meta tags, schema markup, and mobile-first design to help your site rank higher in search engines.</p>
            </motion.div>
            <motion.div
              className="web-development__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3>Can you redesign my existing website?</h3>
              <p>Yes! I specialize in website redesigns. App Developer Pro analyzes your current site, identifies issues, and creates a modern, high-performing website that improves user experience, increases conversions, and aligns with your brand identity.</p>
            </motion.div>
            <motion.div
              className="web-development__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>What technologies do you use for web development?</h3>
              <p>App Developer Pro uses cutting-edge technologies including React, Next.js, Node.js, PHP, Python, Laravel, WordPress, and modern databases like MySQL and MongoDB. I select the best tech stack based on your project requirements, scalability needs, and budget.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="web-development__cta">
        <motion.div
          className="web-development__cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="web-development__cta-title">Ready to Build Your Dream Website?</h2>
          <p className="web-development__cta-text">
            Let's discuss your project and create a custom web solution that drives results for your business.
          </p>
          <div className="web-development__cta-buttons">
            <Link to="/contact" className="web-development__cta-btn web-development__cta-btn--primary">
              Get Free Quote <FaArrowRight />
            </Link>
            <Link to="/portfolio" className="web-development__cta-btn web-development__cta-btn--secondary">
              View Portfolio
            </Link>
          </div>
        </motion.div>
      </section>
      </div>
    </>
  );
};

export default WebDevelopment;
